import { useTranslation } from 'react-i18next';
import type { Location, Playlist, Screen, ScreenGroup } from '@ghostscreen/contracts';
import { formatDaysOfWeek } from '../../lib/schedule/formatDaysOfWeek';
import { formatLocalTime } from '../../lib/schedule/formatLocalWindow';
import { locationNameForScreen } from '../../lib/schedule/timezoneForScreen';
import { DayOfWeekPicker } from './DayOfWeekPicker';

export type ScheduleTargetKind = 'screen' | 'group';

export type ScheduleFormValues = {
  name: string;
  targetKind: ScheduleTargetKind;
  screenId: string;
  screenGroupId: string;
  playlistId: string;
  startLocalTime: string;
  endLocalTime: string;
  daysOfWeek: number[];
  priority: string;
};

type ScheduleFormFieldsProps = {
  values: ScheduleFormValues;
  onChange: (next: ScheduleFormValues) => void;
  screens: Screen[];
  groups: ScreenGroup[];
  playlists: Playlist[];
  locations: Location[];
  targetLocked?: boolean;
  disabled?: boolean;
  showPreview?: boolean;
};

const PRIORITIES = [1, 2, 3, 4, 5] as const;

const labelStyle = { color: 'var(--gs-ink2)' } as const;
const fieldStyle = {
  color: 'var(--gs-ink)',
  background: 'var(--gs-field)',
  borderColor: 'var(--gs-line)',
  fontFamily: 'inherit',
} as const;

/** Form body for the daypart drawer — Ghost Screen Schedule.dc.html. */
export function ScheduleFormFields({
  values,
  onChange,
  screens,
  groups,
  playlists,
  locations,
  targetLocked = false,
  disabled = false,
  showPreview = false,
}: ScheduleFormFieldsProps) {
  const { t } = useTranslation('schedule');
  const set = <K extends keyof ScheduleFormValues>(key: K, value: ScheduleFormValues[K]) =>
    onChange({ ...values, [key]: value });

  const selectedScreen = screens.find((screen) => screen.id === values.screenId);
  const selectedGroup = groups.find((group) => group.id === values.screenGroupId);
  const selectedPlaylist = playlists.find((playlist) => playlist.id === values.playlistId);

  const priorityNum = Number(values.priority) || 3;
  const startPct = timeToPct(values.startLocalTime);
  const endPct = timeToPct(values.endLocalTime);
  const spanPct = Math.max(0.5, endPct - startPct);
  const wraps = values.startLocalTime > values.endLocalTime;

  const targetLabel =
    values.targetKind === 'screen'
      ? (selectedScreen?.name ?? t('form.previewNoTarget'))
      : (selectedGroup?.name ?? t('form.previewNoTarget'));
  const playlistLabel = selectedPlaylist?.name ?? t('form.previewNoPlaylist');
  const nameLabel = values.name.trim() || t('untitledDaypart');
  const daysPhrase =
    values.daysOfWeek.length === 0
      ? t('form.previewEveryDay')
      : formatDaysOfWeek(values.daysOfWeek);

  return (
    <div className="flex flex-col gap-[22px]">
      <label className="flex flex-col gap-2">
        <span className="text-[12.5px] font-bold" style={labelStyle}>
          {t('form.name')}
        </span>
        <input
          value={values.name}
          disabled={disabled}
          onChange={(event) => set('name', event.target.value)}
          placeholder={t('form.namePlaceholder')}
          className="w-full rounded-[10px] border px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
          style={fieldStyle}
        />
      </label>

      <span className="flex flex-col gap-2.5">
        <span className="text-[12.5px] font-bold" style={labelStyle}>
          {t('form.appliesTo')}
        </span>
        {!targetLocked ? (
          <span className="grid grid-cols-2 gap-2">
            {(['screen', 'group'] as const).map((kind) => {
              const active = values.targetKind === kind;
              return (
                <button
                  key={kind}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onChange({
                      ...values,
                      targetKind: kind,
                      screenId: kind === 'screen' ? values.screenId : '',
                      screenGroupId: kind === 'group' ? values.screenGroupId : '',
                    })
                  }
                  className="rounded-[9px] border px-3.5 py-[9px] text-[13px] font-semibold"
                  style={{
                    color: active ? 'var(--gs-ink)' : 'var(--gs-muted)',
                    background: active ? 'var(--gs-panel2)' : 'transparent',
                    borderColor: active ? 'var(--gs-accent)' : 'var(--gs-line)',
                    fontFamily: 'inherit',
                  }}
                >
                  {kind === 'screen' ? t('form.targetScreen') : t('form.targetGroup')}
                </button>
              );
            })}
          </span>
        ) : null}

        <span
          className="flex max-h-[196px] flex-col gap-1.5 overflow-y-auto pe-0.5"
          style={{ scrollbarGutter: 'stable' }}
        >
          {values.targetKind === 'screen'
            ? screens.map((screen) => {
                const active = values.screenId === screen.id;
                const loc = locationNameForScreen(screen, locations);
                return (
                  <TargetOption
                    key={screen.id}
                    active={active}
                    disabled={disabled || targetLocked}
                    label={screen.name}
                    meta={loc ?? t('form.noLocation')}
                    onClick={() => set('screenId', screen.id)}
                  />
                );
              })
            : groups.map((group) => {
                const active = values.screenGroupId === group.id;
                return (
                  <TargetOption
                    key={group.id}
                    active={active}
                    disabled={disabled || targetLocked}
                    label={group.name}
                    meta={t('form.groupScreens', { count: group.memberCount })}
                    onClick={() => set('screenGroupId', group.id)}
                  />
                );
              })}
        </span>
      </span>

      <label className="flex flex-col gap-2">
        <span className="text-[12.5px] font-bold" style={labelStyle}>
          {t('form.playlist')}
        </span>
        <select
          value={values.playlistId}
          disabled={disabled}
          onChange={(event) => set('playlistId', event.target.value)}
          className="w-full cursor-pointer rounded-[10px] border px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
          style={fieldStyle}
          required
        >
          <option value="">{t('form.selectPlaylist')}</option>
          {playlists.map((playlist) => (
            <option key={playlist.id} value={playlist.id}>
              {playlist.name}
            </option>
          ))}
        </select>
      </label>

      <span className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-2">
          <span className="text-[12.5px] font-bold" style={labelStyle}>
            {t('form.start')}
          </span>
          <input
            type="time"
            value={values.startLocalTime}
            disabled={disabled}
            onChange={(event) => set('startLocalTime', event.target.value)}
            className="w-full rounded-[10px] border px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
            style={fieldStyle}
            required
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[12.5px] font-bold" style={labelStyle}>
            {t('form.end')}
          </span>
          <input
            type="time"
            value={values.endLocalTime}
            disabled={disabled}
            onChange={(event) => set('endLocalTime', event.target.value)}
            className="w-full rounded-[10px] border px-3.5 py-3 text-[14.5px] outline-none focus:border-[var(--gs-accent)]"
            style={fieldStyle}
            required
          />
        </label>
      </span>

      <DayOfWeekPicker
        value={values.daysOfWeek}
        disabled={disabled}
        onChange={(days) => set('daysOfWeek', days)}
      />

      <span className="flex flex-col gap-[9px]">
        <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-[12.5px] font-bold" style={labelStyle}>
            {t('form.priority')}
          </span>
          <span className="text-xs" style={{ color: 'var(--gs-muted)' }}>
            {t('form.priorityHint')}
          </span>
        </span>
        <span className="flex flex-wrap gap-1.5">
          {PRIORITIES.map((priority) => {
            const active = priorityNum === priority;
            return (
              <button
                key={priority}
                type="button"
                disabled={disabled}
                onClick={() => set('priority', String(priority))}
                className="rounded-[9px] border px-[13px] py-2 text-[13px] font-semibold"
                style={{
                  color: active ? 'var(--gs-ink)' : 'var(--gs-muted)',
                  background: active ? 'var(--gs-panel2)' : 'transparent',
                  borderColor: active ? 'var(--gs-accent)' : 'var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              >
                {t('priorityBadge', { priority })}
              </button>
            );
          })}
        </span>
      </span>

      {showPreview ? (
        <span
          className="flex flex-col gap-2.5 rounded-xl border px-4 py-[15px]"
          style={{
            background: 'var(--gs-panel2)',
            borderColor: 'var(--gs-line)',
          }}
        >
          <span
            className="text-xs font-bold uppercase tracking-[0.14em]"
            style={{ color: 'var(--gs-muted)' }}
          >
            {t('form.preview')}
          </span>
          <span
            className="relative block h-[30px] overflow-hidden rounded-[7px] border"
            style={{ background: 'var(--gs-canvas)', borderColor: 'var(--gs-line)' }}
          >
            {wraps ? (
              <>
                <span
                  className="absolute bottom-[3px] top-[3px] rounded-[5px]"
                  style={{
                    insetInlineStart: `${startPct}%`,
                    insetInlineEnd: 0,
                    background: 'var(--gs-accent-solid)',
                  }}
                />
                <span
                  className="absolute bottom-[3px] top-[3px] rounded-[5px]"
                  style={{
                    insetInlineStart: 0,
                    width: `${endPct}%`,
                    background: 'var(--gs-accent-solid)',
                  }}
                />
              </>
            ) : (
              <span
                className="absolute bottom-[3px] top-[3px] rounded-[5px]"
                style={{
                  insetInlineStart: `${startPct}%`,
                  width: `${spanPct}%`,
                  background: 'var(--gs-accent-solid)',
                }}
              />
            )}
          </span>
          <span className="text-[12.5px]" style={{ color: 'var(--gs-ink2)', lineHeight: 1.5 }}>
            {t('form.previewSentence', {
              name: nameLabel,
              playlist: playlistLabel,
              target: targetLabel,
              start: formatLocalTime(values.startLocalTime),
              end: formatLocalTime(values.endLocalTime),
              days: daysPhrase,
            })}
          </span>
        </span>
      ) : null}
    </div>
  );
}

function TargetOption({
  active,
  disabled,
  label,
  meta,
  onClick,
}: {
  active: boolean;
  disabled?: boolean;
  label: string;
  meta: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-[11px] rounded-[10px] border px-[13px] py-[11px] text-start"
      style={{
        background: active ? 'var(--gs-panel2)' : 'transparent',
        borderColor: active ? 'var(--gs-accent)' : 'var(--gs-line)',
        fontFamily: 'inherit',
      }}
    >
      <span
        className="grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full border-2"
        style={{ borderColor: active ? 'var(--gs-accent)' : 'var(--gs-line)' }}
        aria-hidden
      >
        <span
          className="h-[7px] w-[7px] rounded-full"
          style={{ background: active ? 'var(--gs-accent)' : 'transparent' }}
        />
      </span>
      <span
        className="min-w-0 flex-1 truncate text-start text-[13.5px] font-semibold"
        style={{ color: 'var(--gs-ink)' }}
      >
        {label}
      </span>
      <span className="shrink-0 text-xs" style={{ color: 'var(--gs-muted)' }}>
        {meta}
      </span>
    </button>
  );
}

function timeToPct(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return (((h ?? 0) * 60 + (m ?? 0)) / (24 * 60)) * 100;
}

export const emptyScheduleForm = (): ScheduleFormValues => ({
  name: '',
  targetKind: 'screen',
  screenId: '',
  screenGroupId: '',
  playlistId: '',
  startLocalTime: '17:00',
  endLocalTime: '22:00',
  daysOfWeek: [],
  priority: '3',
});

export function scheduleToFormValues(schedule: {
  name: string | null;
  screenId: string | null;
  screenGroupId: string | null;
  playlistId: string;
  startLocalTime: string;
  endLocalTime: string;
  daysOfWeek: number[];
  priority: number;
}): ScheduleFormValues {
  const isGroup = Boolean(schedule.screenGroupId);
  return {
    name: schedule.name ?? '',
    targetKind: isGroup ? 'group' : 'screen',
    screenId: schedule.screenId ?? '',
    screenGroupId: schedule.screenGroupId ?? '',
    playlistId: schedule.playlistId,
    startLocalTime: schedule.startLocalTime,
    endLocalTime: schedule.endLocalTime,
    daysOfWeek: [...schedule.daysOfWeek],
    priority: String(schedule.priority),
  };
}
